<script setup lang="ts">
/**
 * Home route.
 *
 * Deletable example content. What is not deletable is the shape: exactly one `<h1>`, sections that
 * nest headings without skipping a level, and no `<main>` — the shell provides that.
 *
 * Two blocks here exist to keep the structured data honest rather than to fill the page. The
 * release line renders the version, licence and price that the `SoftwareApplication` entity claims,
 * and the testimonials section renders the reviews its `aggregateRating` is the mean of. Delete
 * either and the corresponding markup has to go with it — structured data describing content a
 * visitor cannot see is the documented cause of a manual action, not a minor inconsistency.
 *
 * The visual layer is worth one note. The hero's entrance stagger comes from `.enter` and the
 * `--enter-step` custom property, and the `<h1>` deliberately does not take part: it is the largest
 * text on the page and therefore the likely Largest Contentful Paint element, so fading it in would
 * add the fade's duration straight onto that measurement. Everything below the fold uses `.reveal`,
 * which is driven by scroll position in CSS and leaves the content visible where the browser does
 * not support it. See the comments in `src/style.css`.
 */

/**
 * Vue
 */
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import {
  ArrowRight,
  ArrowUpRight,
  Boxes,
  Cog,
  FileCode,
  Globe,
  Map,
  Quote,
  ShieldCheck,
  Sparkles,
  Star,
  Waypoints
} from '@lucide/vue'

/**
 * Components
 */
import AppBackdrop from '@/components/ui/AppBackdrop.vue'
import AppBadge from '@/components/ui/AppBadge.vue'
import AppButton from '@/components/ui/AppButton.vue'
import AppCard from '@/components/ui/AppCard.vue'
import AppContainer from '@/components/ui/AppContainer.vue'
import AppSectionHeader from '@/components/ui/AppSectionHeader.vue'

/**
 * Composables
 */
import { useTilt } from '@bayudwiyansatria/vue/motion'

/**
 * Config
 */
import { softwareConfig } from '@/config/software'
import { testimonials } from '@/config/testimonials'

const { t } = useI18n()

/** Pointer tilt on the output panel. Bails out on coarse pointers and under reduced motion. */
const panel = useTilt({ max: 5 })

/*
 * Keyed by the `home.features.*` translation namespace, in the order `softwareConfig.featureList`
 * declares them. A test pins the two lists together, so reordering here without reordering there is
 * a failing test rather than a graph that describes features the page does not mention.
 */
const features = [
  { key: 'prerender', icon: FileCode },
  { key: 'schema', icon: Waypoints },
  { key: 'generated', icon: Map },
  { key: 'boundaries', icon: ShieldCheck }
] as const

/** The three build stages, as `vite-ssg` actually runs them. */
const pipeline = [
  { key: 'configure', icon: Cog },
  { key: 'build', icon: Boxes },
  { key: 'serve', icon: Globe }
] as const

/*
 * The stack, spelled the way each project spells itself. Not translated: they are proper nouns, and
 * a localised framework name is a name for something that does not exist.
 */
const stack = ['Vue 3', 'Vite', 'TypeScript', 'Tailwind CSS', 'vite-ssg', 'Cloudflare Pages']

/*
 * What the build itself produces in `dist/` — the four routes rendered to HTML, and the three files
 * generated from the site config and that rendered output, which is the distinction the second
 * column names. Whatever `public/` contributes is copied rather than produced, so it is not listed.
 *
 * Adding a route adds a file here. The panel is illustrative rather than derived, which is the trade
 * for keeping the route table out of a view; it is worth a glance whenever that table changes.
 */
const output = [
  { name: 'index.html', origin: 'prerendered' },
  { name: 'about.html', origin: 'prerendered' },
  { name: 'contact.html', origin: 'prerendered' },
  { name: '404.html', origin: 'prerendered' },
  { name: 'sitemap.xml', origin: 'generated' },
  { name: 'robots.txt', origin: 'generated' },
  { name: 'manifest.json', origin: 'generated' }
] as const
</script>

<template>
  <!--
    Full-bleed rather than `AppContainer as="section"`: the backdrop's blobs are wider than the
    content column on purpose, and a container with `overflow-hidden` would slice them off at the
    gutter, leaving a visible vertical edge down each side of the hero.
  -->
  <section class="relative overflow-hidden">
    <AppBackdrop dots />

    <AppContainer class="relative grid items-center gap-14 py-20 md:py-28 lg:grid-cols-[1.1fr_1fr]">
      <div>
        <AppBadge class="enter font-mono uppercase tracking-[0.2em]" style="--enter-step: 0">
          <Sparkles class="h-3.5 w-3.5" aria-hidden="true" />
          {{ t('home.eyebrow') }}
        </AppBadge>

        <!--
          The accent phrase is a placeholder in the message, not a second sentence concatenated onto
          the first. A translator can move `{accent}` to wherever the emphasis belongs in their
          language; two glued-together strings force English word order onto every locale.
        -->
        <i18n-t
          keypath="home.headline"
          tag="h1"
          scope="global"
          class="mt-6 font-headline text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl xl:text-6xl"
        >
          <template #accent>
            <span class="text-gradient">{{ t('home.headlineAccent') }}</span>
          </template>
        </i18n-t>

        <p class="enter mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground" style="--enter-step: 1">
          {{ t('home.subheadline') }}
        </p>

        <div class="enter mt-10 flex flex-wrap gap-3" style="--enter-step: 2">
          <AppButton to="/about" size="lg">
            {{ t('actions.getStarted') }}
            <ArrowRight class="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
          </AppButton>
          <AppButton to="/contact" variant="outline" size="lg">{{ t('nav.contact') }}</AppButton>
        </div>

        <!--
          The facts the SoftwareApplication entity publishes about the release, on the page that
          publishes them. `price: 0` is what the free-and-open-source line stands behind.
        -->
        <div
          v-if="softwareConfig.enabled"
          class="enter mt-10 flex flex-wrap items-center gap-2 text-sm"
          style="--enter-step: 3"
        >
          <AppBadge tone="outline" class="font-mono">{{
            t('home.release.version', { version: softwareConfig.version })
          }}</AppBadge>
          <AppBadge tone="muted" dot>{{ t('home.release.free') }}</AppBadge>
          <a
            :href="softwareConfig.repository"
            rel="noopener noreferrer"
            class="group inline-flex items-center gap-1 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
          >
            {{ t('home.release.source') }}
            <ArrowUpRight
              class="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
        </div>

        <ul class="enter mt-12 flex flex-wrap items-center gap-x-6 gap-y-2" style="--enter-step: 4">
          <li
            v-for="tool in stack"
            :key="tool"
            class="font-mono text-xs uppercase tracking-widest text-muted-foreground/70"
          >
            {{ tool }}
          </li>
        </ul>
      </div>

      <!--
        The hero visual is the build output itself — the seven files `npm run build` writes, four
        prerendered and three generated. A stock illustration would say nothing about the project;
        this says the whole thing, and it cannot become a lie without the build changing.

        Two nested elements, because both carry a transform: `.enter` animates one on load and
        `.tilt-card` reads pointer position into the other. On a single element the later transform
        silently wins and one of the two effects disappears.
      -->
      <div class="enter" style="--enter-step: 2" aria-hidden="true">
        <div
          :ref="panel.targetRef"
          class="tilt-card overflow-hidden rounded-2xl border border-border bg-card/80 shadow-xl shadow-primary/5 backdrop-blur"
        >
          <div class="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-3">
            <span class="h-2.5 w-2.5 rounded-full bg-destructive/50" />
            <span class="h-2.5 w-2.5 rounded-full bg-chart-4/60" />
            <span class="h-2.5 w-2.5 rounded-full bg-primary/50" />
            <span class="ml-2 font-code text-xs text-muted-foreground">dist/</span>
          </div>

          <ul class="divide-y divide-border/60">
            <li v-for="file in output" :key="file.name" class="flex items-center justify-between gap-4 px-4 py-2.5">
              <span class="font-code text-xs text-foreground">{{ file.name }}</span>
              <span class="font-mono text-[0.65rem] uppercase tracking-widest text-muted-foreground/80">
                {{ t(`home.output.${file.origin}`) }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </AppContainer>
  </section>

  <AppContainer as="section" aria-labelledby="features-heading" class="py-16 md:py-24">
    <AppSectionHeader
      id="features-heading"
      :eyebrow="t('home.features.eyebrow')"
      :title="t('home.features.title')"
      :description="t('home.features.lead')"
    />

    <div class="mt-12 grid gap-5 md:grid-cols-2">
      <AppCard
        v-for="(feature, index) in features"
        :key="feature.key"
        :title="t(`home.features.${feature.key}.title`)"
        :heading-level="3"
        interactive
        class="reveal"
        :style="{ '--reveal-step': index % 2 }"
      >
        <template #icon>
          <component :is="feature.icon" class="h-5 w-5" aria-hidden="true" />
        </template>
        {{ t(`home.features.${feature.key}.description`) }}
      </AppCard>
    </div>
  </AppContainer>

  <AppContainer as="section" aria-labelledby="pipeline-heading" class="py-16 md:py-24">
    <AppSectionHeader
      id="pipeline-heading"
      :eyebrow="t('home.pipeline.eyebrow')"
      :title="t('home.pipeline.title')"
      :description="t('home.pipeline.lead')"
    />

    <!--
      An ordered list, because the three stages happen in this order and that is the only thing the
      numbers mean. Styled to look like a row of panels; still read as "1 of 3" by a screen reader.
    -->
    <ol class="mt-12 grid gap-5 md:grid-cols-3">
      <li
        v-for="(stage, index) in pipeline"
        :key="stage.key"
        class="reveal relative rounded-2xl border border-border bg-card/50 p-6 backdrop-blur-sm"
        :style="{ '--reveal-step': index }"
      >
        <span
          class="absolute right-5 top-5 font-headline text-4xl font-bold text-muted-foreground/15"
          aria-hidden="true"
        >
          {{ index + 1 }}
        </span>

        <component :is="stage.icon" class="h-5 w-5 text-primary" aria-hidden="true" />

        <h3 class="mt-4 font-headline text-base font-semibold tracking-tight">
          {{ t(`home.pipeline.${stage.key}.title`) }}
        </h3>
        <p class="mt-2 text-sm leading-relaxed text-muted-foreground">
          {{ t(`home.pipeline.${stage.key}.description`) }}
        </p>
      </li>
    </ol>
  </AppContainer>

  <!--
    Rendered only when there are reviews. The empty state is not a placeholder to fill with sample
    quotes: every entry here becomes a Review node and feeds the aggregateRating, so an invented one
    is a fabricated review published on every site forked from this template.
  -->
  <AppContainer
    v-if="testimonials.length > 0"
    as="section"
    aria-labelledby="testimonials-heading"
    class="py-16 md:py-24"
  >
    <AppSectionHeader
      id="testimonials-heading"
      :eyebrow="t('home.testimonials.eyebrow')"
      :title="t('home.testimonials.title')"
    />

    <ul class="mt-12 grid gap-5 md:grid-cols-2">
      <li v-for="(testimonial, index) in testimonials" :key="testimonial.quote">
        <AppCard class="reveal h-full" :style="{ '--reveal-step': index % 2 }">
          <figure>
            <Quote class="h-6 w-6 text-primary/40" aria-hidden="true" />

            <blockquote class="mt-4 text-base leading-relaxed text-foreground">{{ testimonial.quote }}</blockquote>

            <figcaption class="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/70 pt-4 text-sm">
              <span class="font-semibold text-foreground">{{ testimonial.author }}</span>
              <span v-if="testimonial.role" class="text-muted-foreground">{{ testimonial.role }}</span>

              <!--
                Stars and the number, not stars alone. The aggregate rating is the mean of these
                values, so the exact figure has to be legible — and the icons are decorative
                because the text beside them already says what they encode.
              -->
              <span class="ml-auto inline-flex items-center gap-1 text-primary">
                <Star
                  v-for="star in 5"
                  :key="star"
                  class="h-3.5 w-3.5"
                  :class="star <= testimonial.rating ? 'fill-current' : 'text-muted-foreground/30'"
                  aria-hidden="true"
                />
                <span class="ml-1 text-xs text-muted-foreground">
                  {{ t('home.testimonials.rating', { rating: testimonial.rating }) }}
                </span>
              </span>
            </figcaption>
          </figure>
        </AppCard>
      </li>
    </ul>
  </AppContainer>

  <AppContainer as="section" aria-labelledby="cta-heading" class="pb-24 pt-8">
    <div class="reveal relative overflow-hidden rounded-3xl border border-border bg-card/60 px-6 py-14 text-center">
      <AppBackdrop />

      <div class="relative mx-auto max-w-xl">
        <h2 id="cta-heading" class="font-headline text-2xl font-bold tracking-tight md:text-3xl">
          {{ t('home.cta.title') }}
        </h2>
        <p class="mt-4 text-muted-foreground">{{ t('home.cta.lead') }}</p>

        <div class="mt-8 flex flex-wrap justify-center gap-3">
          <AppButton :href="softwareConfig.repository" size="lg">
            {{ t('home.cta.action') }}
            <ArrowUpRight class="h-4 w-4 transition group-hover:-translate-y-0.5" aria-hidden="true" />
          </AppButton>
          <AppButton to="/contact" variant="outline" size="lg">{{ t('nav.contact') }}</AppButton>
        </div>
      </div>
    </div>
  </AppContainer>
</template>
